// Status chips of the Assessments area, icon and words and never colour alone: an assessment's stage (queued or running with the topics done from its live run job named in the list, or "Run stopped" once that job has ended or is gone and a list fetched since (its time given by AssessmentsListedAt) still says it runs, ready for review, in review with the topics reviewed, approved with who signed it off, failed with the reason in plain words, cancelled) and a capability document's reading state (reading with the live page, ready, failed with the reason, or not read yet with the reason when it is known), plus relative times with the exact time for assistive technology and on hover.
import { createContext, use, type ReactNode } from "react";

import type { AssessmentSummary, CapabilityDocumentSummary } from "@/api/__generated__";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { absoluteTime, relativeTime } from "@/features/datasets/labels";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";

import {
  assessmentStage,
  itemsFraction,
  READING_CHIPS,
  readingProgressLine,
  reviewedCount,
  reviewLine,
  RUN_STOPPED_CHIP,
  runningLabel,
  STAGE_CHIPS,
  type ChipStyle,
} from "./labels";
import { useFollowedJob, useReadingProblem, useRunJob } from "./queries";

export function StatusChip({
  style,
  label,
  className,
}: {
  style: ChipStyle;
  label?: string;
  className?: string;
}) {
  const Icon = style.icon;
  return (
    <Badge variant={style.variant} className={className}>
      <Icon
        aria-hidden
        className={cn(style.spinning === true && "animate-spin motion-reduce:animate-none")}
      />
      {label ?? style.label}
    </Badge>
  );
}

function SubLine({ children, title }: { children: string; title?: string }) {
  return (
    <span className="line-clamp-2 max-w-[260px] text-xs text-muted-foreground" title={title}>
      {children}
    </span>
  );
}

type Align = "start" | "end";

const ALIGN: Record<Align, string> = { start: "items-start", end: "items-end text-right" };

const ListedAt = createContext(0);

export function AssessmentsListedAt({ value, children }: { value: number; children: ReactNode }) {
  return <ListedAt value={value}>{children}</ListedAt>;
}

function RunningStatus({ assessment, align }: { assessment: AssessmentSummary; align: Align }) {
  const listedAt = use(ListedAt);
  const { job, endedAt } = useRunJob(assessment.job_id);
  const total = assessment.topics_total;
  if (endedAt !== null && listedAt > endedAt) {
    return (
      <span className={cn("flex flex-col gap-1", ALIGN[align])}>
        <StatusChip style={RUN_STOPPED_CHIP} />
        <SubLine>Open the assessment to run it again.</SubLine>
      </span>
    );
  }
  const label =
    assessment.status === "queued" && job === undefined
      ? STAGE_CHIPS.queued.label
      : runningLabel(job, { done: assessment.review.total, total });
  const fraction =
    itemsFraction(job?.progress ?? null) ?? (total > 0 ? assessment.review.total / total : null);
  return (
    <span className={cn("flex flex-col gap-1.5", ALIGN[align])}>
      <StatusChip style={STAGE_CHIPS[label === "Queued" ? "queued" : "running"]} label={label} />
      {fraction === null ? null : (
        <Progress
          value={Math.round(fraction * 100)}
          className="h-1 w-24"
          aria-label={`${assessment.name}: ${label}`}
        />
      )}
    </span>
  );
}

export function AssessmentStatus({
  assessment,
  align = "start",
}: {
  assessment: AssessmentSummary;
  align?: Align;
}) {
  const stage = assessmentStage(assessment);
  if (stage === "queued" || stage === "running") {
    return <RunningStatus assessment={assessment} align={align} />;
  }
  const style = STAGE_CHIPS[stage];
  return (
    <span className={cn("flex flex-col gap-1", ALIGN[align])}>
      <StatusChip style={style} />
      {stage === "in_review" ? (
        <span className="flex items-center gap-2">
          <Progress
            value={
              assessment.review.total === 0
                ? 0
                : Math.round((reviewedCount(assessment.review) / assessment.review.total) * 100)
            }
            className="h-1 w-12"
            aria-label={`${assessment.name}: ${reviewLine(assessment.review)}`}
          />
          <span className="text-xs whitespace-nowrap text-muted-foreground tabular-nums">
            {reviewLine(assessment.review)}
          </span>
        </span>
      ) : null}
      {stage === "approved" && assessment.approved_by_name !== null ? (
        <SubLine>{`by ${assessment.approved_by_name} · ${formatDate(assessment.approved_at)}`}</SubLine>
      ) : null}
      {stage === "failed" && assessment.error !== null ? (
        <SubLine title={assessment.error}>{assessment.error}</SubLine>
      ) : null}
    </span>
  );
}

type ReadingDocument = Pick<CapabilityDocumentSummary, "capability_doc_uid" | "status" | "job_id" | "error">;

export function useReadingLine(document: ReadingDocument): string | null {
  const job = useFollowedJob(document.status === "extracting" ? document.job_id : null);
  const remembered = useReadingProblem(document.capability_doc_uid);
  switch (document.status) {
    case "extracting":
      return job === undefined ? null : readingProgressLine(job);
    case "failed":
      return document.error ?? "Reading failed. Read the document again.";
    case "uploaded":
      return remembered?.title ?? document.error ?? "Reading has not started.";
    case "ready":
      return null;
  }
}

export function ReadingStatus({ document, live = false }: { document: ReadingDocument; live?: boolean }) {
  const line = useReadingLine(document);
  return (
    <span className="flex flex-col items-start gap-1" aria-live={live ? "polite" : undefined}>
      <StatusChip style={READING_CHIPS[document.status]} />
      {line === null ? null : <SubLine title={line}>{line}</SubLine>}
    </span>
  );
}

export function RelativeTime({ value, className }: { value: string | null; className?: string }) {
  if (value === null) {
    return <span className={className}>–</span>;
  }
  const exact = absoluteTime(value);
  return (
    <time dateTime={value} title={exact} className={cn("whitespace-nowrap", className)}>
      {relativeTime(value)}
      <span className="sr-only"> ({exact})</span>
    </time>
  );
}
